import { extensions } from "@repo/fika-blog/src/extensions"
import { renderToText } from "@repo/fika-blog/src/lib/render-to-text"
import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import { cdnClass } from "@repo/fika-design-system/cdn-class"
import { parseLocale } from "@repo/fika-shared/src/locale"
import { translate } from "@repo/fika-shared/src/translate"
import { createQuery } from "@urql/solid"
import { format } from "date-fns"
import { createMemo, createSignal, For, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Dialog } from "@/designSystem/components/dialog"
import { Icon } from "@/designSystem/components/icon"
import {
  PageInfoFragment,
  Pagination,
} from "@/designSystem/components/pagination"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import { PostCard, PostCardFragment } from "@/designSystem/patterns/post-card"
import { readMoreButtonClass } from "@/designSystem/patterns/post-card.css"
import {
  createFragment,
  type FragmentOf,
  graphql,
  readFragment,
} from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  getReactionSummary,
  PublishedPostReactionSummaryFragment,
} from "@/lib/get-reaction-summary"
import { toast } from "@/lib/toast"
import { SubscriptionPrompt } from "@/pages/auth/subscription-prompt"
import { createSubscriptionsRouter } from "@/routes/subscriptions-router"
import { ContentAccessBadgeFragment } from "./content-access-badge"
import { mainClass } from "./subscription-list.css"

const PublishedPostFragment = PostCardFragment

export function SubscriptionList() {
  const { LL } = useI18nContext()
  const { paths, listParams } = createSubscriptionsRouter()

  const [changingPlanForPost, setChangingPlanForPost] = createSignal<FragmentOf<
    typeof PublishedPostFragment
  > | null>(null)
  const changingPlan = createMemo(() => changingPlanForPost() !== null)

  function handleTierSelectionSuccess() {
    setChangingPlanForPost(null)
    executeQuery({ requestPolicy: "network-only" })
  }

  const variables = createMemo(() => {
    const params = listParams()
    if (params.before) {
      return { before: params.before, last: 20 }
    }
    return { after: params.after || undefined, first: 20 }
  })

  const PublishedPostsQuery = graphql(
    `query PublishedPosts($after: String, $before: String, $first: Int, $last: Int) {
      publishedPosts(subscribed: true, after: $after, before: $before, first: $first, last: $last) {
        edges {
          cursor
          node {
            ...PostCard
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
      }
    }`,
    [PublishedPostFragment, PageInfoFragment],
  )
  const [result, executeQuery] = createQuery({
    query: PublishedPostsQuery,
    variables,
    requestPolicy: "cache-and-network",
  })

  const buildUrl = (params: { after: string; before: string }) =>
    paths(params).url

  const activePost = createMemo(() => {
    const postData = changingPlanForPost()
    if (!postData) return null
    return readFragment(PublishedPostFragment, postData)
  })

  return (
    <MainLayout
      hue="indigo"
      header={
        <NavigationWithButtons title={LL().subscriptions.title()} hue="indigo">
          <LinkButton
            hue="indigo"
            kind="surface"
            href={paths().manage().url}
            iconName="settings"
            aria-label={LL().subscriptions.manage()}
          />
        </NavigationWithButtons>
      }
      query={result}
    >
      {data => {
        const publishedPosts = createMemo(() => data?.publishedPosts)
        const edges = createMemo(() => publishedPosts()?.edges ?? [])
        const pageInfo = createMemo(() => publishedPosts()?.pageInfo)

        return (
          <>
            <Dialog
              open={changingPlan()}
              onOpenChange={open => {
                if (!open) setChangingPlanForPost(null)
              }}
              size="medium"
            >
              <Show when={activePost()}>
                {post => (
                  <SubscriptionPrompt
                    tiers={post().publication.subscriptionTiers ?? []}
                    publicationId={post().publication.id}
                    userId={post().publication.author.id}
                    onSuccess={handleTierSelectionSuccess}
                    onError={error => {
                      console.error("Tier selection error:", error)
                      toast.error(LL().subscriptions.tierChangeError())
                    }}
                    subscriptionId={post().publication.sub?.id}
                    currentTierName={post().publication.sub?.tier?.name}
                  />
                )}
              </Show>
            </Dialog>

            <div class={mainClass}>
              <Show
                when={edges().length > 0}
                fallback={
                  <Box gap="16" alignItems="center" padding="32">
                    <Icon name="newspaper" size="32" color="sand400" />
                    <Text color="sand600" fontSize="m">
                      {LL().subscriptions.noPublishedPostsYet()}
                    </Text>
                  </Box>
                }
              >
                <For each={edges()}>
                  {edge => (
                    <PublishedPostCard
                      post={edge.node}
                      cursor={edge.cursor}
                      onChangePlan={postData =>
                        setChangingPlanForPost(postData)
                      }
                    />
                  )}
                </For>
                <Pagination pageInfo={pageInfo()} buildUrl={buildUrl} />
              </Show>
            </div>
          </>
        )
      }}
    </MainLayout>
  )
}

function PublishedPostCard(props: {
  post: FragmentOf<typeof PublishedPostFragment>
  onChangePlan: (post: FragmentOf<typeof PublishedPostFragment>) => void
  cursor: string
}) {
  const { LL, locale } = useI18nContext()
  const interfaceLocale = createMemo(() => parseLocale(locale()))
  const post = createFragment(PublishedPostFragment, () => props.post)
  const accessData = createFragment(ContentAccessBadgeFragment, post)
  const { paths } = createSubscriptionsRouter()
  const formatDate = createMemo(() => {
    const date = post().firstPublishedAt
    if (!date) return null
    return format(date, "PPp")
  })

  const postUrl = createMemo(() =>
    paths().post({ id: post().id, title: post().title }, props.cursor),
  )

  const excerpt = createMemo(() => {
    const json = post().content
    if (!json) return ""
    try {
      return renderToText({
        extensions: extensions({ dropcursor: false }),
        doc: JSON.parse(json),
      }).slice(0, 300)
    } catch {
      return ""
    }
  })

  const publicationAvatarUrl = createMemo(
    () =>
      post().publication.logoUrl ??
      post().publication.author.profileImage ??
      getDefaultAvatar(post().publication.id),
  )

  const reactions = createMemo(() =>
    getReactionSummary(
      readFragment(PublishedPostReactionSummaryFragment, post()).reactions,
    ),
  )

  const isRestricted = createMemo(() => accessData().contentAccess.restricted)
  const hasAccess = createMemo(() => accessData().contentAccess.hasAccess)
  const needsUpgrade = createMemo(() => isRestricted() && !hasAccess())

  return (
    <PostCard
      hue="indigo"
      href={postUrl()}
      publicationAvatarUrl={publicationAvatarUrl()}
      publicationName={
        translate(post().publication.title, interfaceLocale()) ??
        post().publication.name
      }
      imageSrc={post().imageUrl ? cdnClass(post().imageUrl!, "post800") : null}
      title={post().title}
      description={post().description}
      excerpt={excerpt() || null}
      publishedDate={formatDate()}
      reactions={reactions()}
      isHumanContent={Boolean(post().humanContent)}
      footerAction={
        <Show
          when={needsUpgrade()}
          fallback={
            <Button
              hue="sand"
              kind="surface"
              size="readable"
              class={readMoreButtonClass}
            >
              {LL().discover.readMore()}
            </Button>
          }
        >
          <Button
            hue="indigo"
            kind="surface"
            size="readable"
            iconName="lock"
            class={readMoreButtonClass}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              props.onChangePlan(props.post)
            }}
          >
            {LL().discover.upgradeToRead()}
          </Button>
        </Show>
      }
    />
  )
}
